import { isNil } from 'lodash'
import { CoolingAirHeatPumpParameters } from '../../generated/graphql'
import { EnergyType } from '../EnergyFlow'
import {
  createHourlyProvided,
  createHourlyRequirement,
  createSecondaryHourlyWaste,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
} from '../HourlyEnergyFlow'
import { OutdoorTemperatureMissingError } from '../calculator-error'
import {
  SingleTypeHourlyVariables,
  SingleTypeProvider,
} from './common/SingleTypeProvider'

type NonFreeCoolingParameters = {
  freeCooling: false
  eer: number
  maxPower: number
}

type FreeCoolingParameters = Omit<NonFreeCoolingParameters, 'freeCooling'> & {
  freeCooling: true
  freeCoolingMaxOutdoorTemperature: number
  freeCoolingEer: number
  freeCoolingMaxPower: number
}

/**
 * Converts electricity to cooling with a limited power.
 */
export class CoolingAirHeatPumpProvider extends SingleTypeProvider<CoolingAirHeatPumpParameters> {
  public readonly providedEnergyTypes = [EnergyType.Cooling]

  private validatedParameters!: FreeCoolingParameters | NonFreeCoolingParameters

  protected initialize(): void {
    const {
      eer,
      maxPower,
      freeCooling,
      freeCoolingMaxOutdoorTemperature,
      freeCoolingEer,
      freeCoolingMaxPower,
    } = this.parameters

    if (isNil(maxPower)) {
      throw new Error('Max power must be defined')
    }

    if (!freeCooling) {
      this.validatedParameters = { eer, maxPower, freeCooling }
      return
    }

    if (
      isNil(freeCoolingMaxOutdoorTemperature) ||
      isNil(freeCoolingEer) ||
      isNil(freeCoolingMaxPower)
    ) {
      throw new Error('Free cooling parameters must be defined')
    }

    this.validatedParameters = {
      eer,
      maxPower,
      freeCooling,
      freeCoolingMaxOutdoorTemperature,
      freeCoolingEer,
      freeCoolingMaxPower,
    }
  }

  protected provideForHour({
    remainingDemand,
    outdoorTemperature,
  }: SingleTypeHourlyVariables): HourlyEnergyFlow[] {
    if (this.validatedParameters.freeCooling) {
      if (isNil(outdoorTemperature)) {
        throw new OutdoorTemperatureMissingError()
      }

      return this.provideFreeCooling(
        this.validatedParameters,
        remainingDemand,
        outdoorTemperature,
      )
    }

    return this.provideNonFreeCooling(this.validatedParameters, remainingDemand)
  }

  private provideFreeCooling(
    parameters: FreeCoolingParameters,
    remainingDemand: PrimaryHourlyEnergyFlow,
    outdoorTemperature: number,
  ): HourlyEnergyFlow[] {
    const {
      eer,
      maxPower,
      freeCoolingMaxOutdoorTemperature,
      freeCoolingEer,
      freeCoolingMaxPower,
    } = parameters

    const isFreeCoolingActive =
      outdoorTemperature < freeCoolingMaxOutdoorTemperature

    const provided = createHourlyProvided(
      remainingDemand.demandId,
      remainingDemand.energyType,
      isFreeCoolingActive
        ? Math.min(remainingDemand.value, freeCoolingMaxPower)
        : Math.min(remainingDemand.value, maxPower),
    )

    const requirement = createHourlyRequirement(
      provided.demandId,
      EnergyType.Electricity,
      provided.value / (isFreeCoolingActive ? freeCoolingEer : eer),
    )

    const waste = createSecondaryHourlyWaste(
      provided.demandId,
      EnergyType.Heating,
      provided.value + requirement.value,
    )

    return [provided, waste, requirement]
  }

  private provideNonFreeCooling(
    parameters: NonFreeCoolingParameters,
    remainingDemand: PrimaryHourlyEnergyFlow,
  ): HourlyEnergyFlow[] {
    const { eer, maxPower } = parameters

    const provided = createHourlyProvided(
      remainingDemand.demandId,
      remainingDemand.energyType,
      Math.min(remainingDemand.value, maxPower),
    )

    const requirement = createHourlyRequirement(
      provided.demandId,
      EnergyType.Electricity,
      provided.value / eer,
    )

    const waste = createSecondaryHourlyWaste(
      provided.demandId,
      EnergyType.Heating,
      provided.value + requirement.value,
    )

    return [provided, waste, requirement]
  }
}
